// Form spec builder — turns an OpenAPI Operation into a concrete form descriptor
// that the TryItForm component can render.
//
// Returns FormSpec with arrays of path/query/header params (typed for the UI)
// plus a resolved bodySchema (with $refs flattened one level).

import type { OpenApiSpec, Operation, Parameter, Schema } from './types';
import { resolveSchema } from './parse';

export type FieldType = 'string' | 'number' | 'boolean' | 'select';

export interface FormField {
    name: string;
    type: FieldType;
    required: boolean;
    description?: string;
    default?: unknown;
    enum?: unknown[];
    example?: unknown;
    format?: string;
}

export interface FormSpec {
    pathParams: FormField[];
    queryParams: FormField[];
    headerParams: FormField[];
    bodySchema: Schema | null;
    bodySample: unknown;
    hasBody: boolean;
}

function inferType(schema: Schema | undefined): FieldType {
    if (!schema) return 'string';
    if (schema.enum && schema.enum.length > 0) return 'select';
    switch (schema.type) {
        case 'integer':
        case 'number':
            return 'number';
        case 'boolean':
            return 'boolean';
        default:
            return 'string';
    }
}

function paramToField(p: Parameter): FormField {
    const s = p.schema || {};
    return {
        name: p.name,
        type: inferType(s),
        required: Boolean(p.required),
        description: p.description,
        default: s.default,
        enum: s.enum,
        example: s.example,
        format: s.format,
    };
}

// Build a sample JSON value from a schema. Mirrors EndpointPage's helper but exposed
// here so TryItForm can pre-fill the body editor without duplicating logic.
export function buildSampleFromSchema(spec: OpenApiSpec, schema: Schema | undefined): unknown {
    if (!schema) return null;
    if (schema.example !== undefined) return schema.example;
    if (schema.$ref) return buildSampleFromSchema(spec, resolveSchema(spec, schema));
    if (schema.type === 'object' && schema.properties) {
        const obj: Record<string, unknown> = {};
        for (const [key, prop] of Object.entries(schema.properties)) {
            obj[key] = buildSampleFromSchema(spec, prop);
        }
        return obj;
    }
    if (schema.type === 'array' && schema.items) {
        return [buildSampleFromSchema(spec, schema.items)];
    }
    if (schema.enum && schema.enum.length > 0) return schema.enum[0];
    if (schema.default !== undefined) return schema.default;
    switch (schema.type) {
        case 'string':
            return schema.format === 'date-time' ? '2026-05-19T10:00:00Z' : '';
        case 'number':
        case 'integer':
            return 0;
        case 'boolean':
            return false;
        case 'null':
            return null;
        default:
            return null;
    }
}

export function buildFormSpec(op: Operation, spec: OpenApiSpec): FormSpec {
    const params = op.parameters || [];
    const pathParams = params.filter((p) => p.in === 'path').map(paramToField);
    const queryParams = params.filter((p) => p.in === 'query').map(paramToField);
    // Authorization is auto-injected — don't surface it as an editable field.
    const headerParams = params
        .filter((p) => p.in === 'header' && p.name.toLowerCase() !== 'authorization')
        .map(paramToField);

    const rawBodySchema = op.requestBody?.content?.['application/json']?.schema;
    const bodySchema = rawBodySchema ? resolveSchema(spec, rawBodySchema) : null;
    const hasBody = Boolean(bodySchema);
    const bodySample = bodySchema ? buildSampleFromSchema(spec, bodySchema) : undefined;

    return {
        pathParams,
        queryParams,
        headerParams,
        bodySchema,
        bodySample,
        hasBody,
    };
}

/** Substitute `{name}` placeholders in a path with provided values. */
export function substitutePath(path: string, values: Record<string, string>): string {
    return path.replace(/\{([^}]+)\}/g, (_, name: string) => {
        const v = values[name];
        if (v === undefined || v === '') return `{${name}}`;
        return encodeURIComponent(v);
    });
}

/** Build query string from a map; skips empty values. */
export function buildQueryString(values: Record<string, string>): string {
    const parts: string[] = [];
    for (const [k, v] of Object.entries(values)) {
        if (v === undefined || v === null || v === '') continue;
        parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    }
    return parts.length ? `?${parts.join('&')}` : '';
}
